"use server";

import { revalidatePath } from "next/cache";
import { prisma } from "@/lib/db";
import { requireAdmin } from "@/lib/admin-guard";

function normPath(s: string): string {
  let p = s.trim();
  if (!p) return p;
  if (!p.startsWith("/") && !/^https?:\/\//i.test(p)) p = "/" + p;
  // Strip trailing slash unless root
  if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");
  return p;
}

async function logAudit(
  user: { id: string; name: string; email: string },
  action: string,
  target: string,
  meta: Record<string, unknown>,
) {
  await prisma.auditLog.create({
    data: {
      actorId: user.id,
      actorName: user.name,
      actorEmail: user.email,
      source: "admin",
      action,
      target,
      meta: meta as never,
    },
  });
}

export async function createRedirectAction(formData: FormData) {
  const user = await requireAdmin();
  const fromPath = normPath(String(formData.get("fromPath") ?? ""));
  const toPath = normPath(String(formData.get("toPath") ?? ""));
  const status = Number(formData.get("statusCode") ?? 301);
  const enabled = formData.get("enabled") !== "off";

  if (!fromPath || !toPath)
    return { ok: false as const, error: "Cả From và To đều bắt buộc." };
  if (fromPath === toPath)
    return { ok: false as const, error: "From và To không được trùng nhau." };
  if (![301, 302, 307, 308].includes(status))
    return { ok: false as const, error: "Status phải là 301, 302, 307 hoặc 308." };

  const exist = await prisma.redirect.findUnique({ where: { fromPath } });
  if (exist) return { ok: false as const, error: `Đã có redirect từ "${fromPath}".` };

  const r = await prisma.redirect.create({
    data: { fromPath, toPath, statusCode: status, enabled },
  });
  await logAudit(user, "redirect.create", r.id, { fromPath, toPath, statusCode: status });
  revalidatePath("/admin/super-seo/redirects");
  return { ok: true as const, id: r.id };
}

export async function updateRedirectAction(id: string, formData: FormData) {
  const user = await requireAdmin();
  const fromPath = normPath(String(formData.get("fromPath") ?? ""));
  const toPath = normPath(String(formData.get("toPath") ?? ""));
  const status = Number(formData.get("statusCode") ?? 301);
  const enabled = formData.get("enabled") === "on";

  if (!fromPath || !toPath)
    return { ok: false as const, error: "Cả From và To đều bắt buộc." };
  if (fromPath === toPath)
    return { ok: false as const, error: "From và To không được trùng nhau." };

  const conflict = await prisma.redirect.findFirst({
    where: { fromPath, NOT: { id } },
    select: { id: true },
  });
  if (conflict) return { ok: false as const, error: `Đã có redirect khác từ "${fromPath}".` };

  await prisma.redirect.update({
    where: { id },
    data: { fromPath, toPath, statusCode: status, enabled },
  });
  await logAudit(user, "redirect.update", id, { fromPath, toPath, statusCode: status, enabled });
  revalidatePath("/admin/super-seo/redirects");
  return { ok: true as const };
}

export async function deleteRedirectAction(id: string) {
  const user = await requireAdmin();
  const r = await prisma.redirect.findUnique({
    where: { id },
    select: { fromPath: true, toPath: true },
  });
  if (!r) return { ok: false as const, error: "Không tìm thấy." };
  await prisma.redirect.delete({ where: { id } });
  await logAudit(user, "redirect.delete", id, r);
  revalidatePath("/admin/super-seo/redirects");
  return { ok: true as const };
}

export async function toggleRedirectAction(id: string, enabled: boolean) {
  const user = await requireAdmin();
  await prisma.redirect.update({ where: { id }, data: { enabled } });
  await logAudit(user, "redirect.toggle", id, { enabled });
  revalidatePath("/admin/super-seo/redirects");
  return { ok: true as const };
}
